import React, { useState } from "react";
import BirthProfileFields from "./BirthProfileFields";
import GunaScoreDial from "./GunaScoreDial";
import { Card, SectionHeading, PrimaryButton, ErrorBanner } from "./ui";
import { createMatch } from "../api/kundliApi";
import { usePremiumGate } from "../hooks/usePremiumGate";

const empty = { name: "", gender: "male", dob: "", tob: "", place: "", timezone: 5.5 };

/** The 8 Ashtakoot kootas with their standard max points (total = 36). */
const KOOTA_CONFIG = [
  { key: "varna",        label: "Varna",        max: 1 },
  { key: "vasya",        label: "Vasya",        max: 2 },
  { key: "tara",         label: "Tara",         max: 3 },
  { key: "yoni",         label: "Yoni",         max: 4 },
  { key: "graha_maitri", label: "Graha Maitri", max: 5 },
  { key: "gana",         label: "Gana",         max: 6 },
  { key: "bhakoot",      label: "Bhakoot",      max: 7 },
  { key: "nadi",         label: "Nadi",         max: 8 },
];


/**
 * Main Kundli Match Form Component
 */
export default function KundliMatchForm() {
  const [personA, setPersonA] = useState(empty);
  const [personB, setPersonB] = useState(empty);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const { runGated, gateModal, priceError } = usePremiumGate("match", "Kundli Milan Compatibility Report");

  const performMatch = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await createMatch(personA, personB);
      setResult(res.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    runGated(performMatch);
  };

  return (
    <div style={styles.layoutWrapper}>
      
      {/* LEFT COLUMN: Main Form */}
      <div style={styles.leftColumn}>
        <Card style={{ maxWidth: "100%" }}> {/* Changed from 920 to 100% to fill the flex column */}
          <SectionHeading
            eyebrow="Ashtakoot Guna Milan"
            title="Kundli Match"
            subtitle="Enter both partners' exact birth details to see their 36-point compatibility score."
          />

          <form onSubmit={handleSubmit}>
            <div style={styles.row}>
              <div style={{ flex: 1, minWidth: 260 }}>
                <BirthProfileFields label="Person A" value={personA} onChange={setPersonA} />
              </div>
              <div style={{ flex: 1, minWidth: 260 }}>
                <BirthProfileFields label="Person B" value={personB} onChange={setPersonB} />
              </div>
            </div>

            <div style={{ textAlign: "center", marginTop: 22 }}>
              <PrimaryButton type="submit" loading={loading} loadingText="Matching…">
                ✦ Match Kundli
              </PrimaryButton>
            </div>
          </form>

          <ErrorBanner message={error || priceError} />

          {result && (
            <div style={styles.result}>
              <GunaScoreDial score={result.total_gun_score} />

              {/* Advisory message */}
              {result.match_data?.message && (
                <div style={{
                  ...styles.messageBox,
                  borderLeft: `4px solid ${result.match_data.message.type === "good" ? "var(--success)" : "var(--terracotta)"}`,
                }}>
                  <span style={styles.messageIcon}>
                    {result.match_data.message.type === "good" ? "✅" : "⚠️"}
                  </span>
                  <p style={styles.messageText}>{result.match_data.message.description}</p>
                </div>
              )}

              {/* Koota comparison table */}
              {result.match_data?.boy_info?.koot && result.match_data?.girl_info?.koot && (
                <div style={styles.kootaTableWrap}>
                  <h4 style={styles.kootaHeading}>Ashtakoot Koota Details</h4>
                  <table style={styles.kootaTable}>
                    <thead>
                      <tr>
                        <th style={styles.kootaTh}>Koota</th>
                        <th style={styles.kootaTh}>Boy</th>
                        <th style={styles.kootaTh}>Girl</th>
                        <th style={{ ...styles.kootaTh, textAlign: "center" }}>Max Points</th>
                      </tr>
                    </thead>
                    <tbody>
                      {KOOTA_CONFIG.map((k) => (
                        <tr key={k.key}>
                          <td style={styles.kootaTd}>
                            <span style={styles.kootaName}>{k.label}</span>
                          </td>
                          <td style={styles.kootaTd}>
                            {result.match_data.boy_info.koot[k.key]}
                          </td>
                          <td style={styles.kootaTd}>
                            {result.match_data.girl_info.koot[k.key]}
                          </td>
                          <td style={{ ...styles.kootaTd, textAlign: "center" }}>
                            <span style={styles.maxPoints}>{k.max}</span>
                          </td>
                        </tr>
                      ))}
                      <tr style={styles.totalRow}>
                        <td colSpan={3} style={{ ...styles.kootaTd, fontWeight: 700, textAlign: "right", paddingRight: 12 }}>
                          Total Score
                        </td>
                        <td style={{ ...styles.kootaTd, textAlign: "center" }}>
                          <span style={styles.totalScore}>
                            {result.match_data.guna_milan?.total_points} / {result.match_data.guna_milan?.maximum_points}
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              {result.pdf_url && (
                <a href={result.pdf_url} target="_blank" rel="noopener noreferrer" style={styles.pdfBtn}>
                  📄 Download full PDF report
                </a>
              )}
            </div>
          )}

          {gateModal}
        </Card>
      </div>

      {/* RIGHT COLUMN: Promo Banner */}
      <div style={styles.rightColumn}>
      </div>

    </div>
  );
}

const styles = {
  // --- Form Styles ---
  row: { display: "flex", gap: 20, flexWrap: "wrap" },
  result: {
    marginTop: 26, background: "var(--paper-2)", borderRadius: "var(--radius)",
    padding: "24px 20px", display: "flex", flexDirection: "column", alignItems: "center",
    animation: "km-fade-up 0.35s var(--ease)",
  },
  messageBox: {
    display: "flex", alignItems: "flex-start", gap: 10, width: "100%", maxWidth: 560,
    marginTop: 18, padding: "14px 16px", borderRadius: "var(--radius-sm)",
    background: "#fffaf5",
  },
  messageIcon: { fontSize: 18, flexShrink: 0, marginTop: 1 },
  messageText: { margin: 0, fontSize: 13.5, lineHeight: 1.55, color: "var(--ink)" },
  kootaTableWrap: { width: "100%", maxWidth: 560, marginTop: 20, overflowX: "auto" },
  kootaHeading: {
    margin: "0 0 10px", fontSize: 15, fontWeight: 700,
    fontFamily: "var(--font-serif)", color: "var(--ink)",
  },
  kootaTable: { width: "100%", borderCollapse: "collapse" },
  kootaTh: {
    textAlign: "left", padding: "9px 10px", fontSize: 12, fontWeight: 700,
    color: "var(--ink-soft)", textTransform: "uppercase", letterSpacing: 0.4,
    borderBottom: "2px solid var(--line)", background: "var(--paper-2)",
  },
  kootaTd: {
    padding: "9px 10px", fontSize: 13.5, color: "var(--ink)",
    borderBottom: "1px solid var(--line)",
  },
  kootaName: { fontWeight: 600 },
  maxPoints: {
    display: "inline-block", background: "var(--paper-2)", color: "var(--terracotta-dark)",
    fontWeight: 700, padding: "2px 8px", borderRadius: 999, fontSize: 12,
  },
  totalRow: { background: "var(--paper-2)" },
  totalScore: {
    fontWeight: 800, fontSize: 14, color: "var(--terracotta-dark)",
  },
  pdfBtn: {
    display: "inline-block", marginTop: 18, padding: "10px 22px", background: "var(--success)",
    color: "#fff", borderRadius: 999, textDecoration: "none", fontWeight: 700, fontSize: 13.5,
  },

  // --- New Layout & Banner Styles ---
  layoutWrapper: {
    display: "flex",
    flexWrap: "wrap",
    gap: "24px",
    maxWidth: "1250px", // Increased to accommodate both sections
    margin: "0 auto",
    alignItems: "flex-start",
  },
  leftColumn: {
    flex: "1 1 65%", 
    minWidth: "300px",
  },
  rightColumn: {
    flex: "1 1 300px", 
    maxWidth: "380px",
    position: "sticky", // Keeps the banner in view when scrolling the form
    top: "24px",
  },
  bannerContainer: {
    backgroundColor: "#1a0b1c",
    color: "#ffffff",
    borderRadius: "16px",
    padding: "24px",
    boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
    border: "1px solid #d4af37",
    fontFamily: "sans-serif",
  },
  badgeWrapper: { textAlign: "center", marginBottom: "16px" },
  badge: {
    backgroundColor: "#b91c1c",
    color: "#fff",
    fontSize: "11px",
    fontWeight: "bold",
    padding: "4px 12px",
    borderRadius: "99px",
    textTransform: "uppercase",
    letterSpacing: "1px",
  },
  bannerTitle: {
    fontSize: "22px",
    fontWeight: "900",
    textAlign: "center",
    color: "#d4af37",
    margin: "0 0 6px 0",
  },
  bannerSubtitle: {
    fontSize: "15px",
    textAlign: "center",
    fontWeight: "600",
    margin: "0 0 20px 0",
  },
  imagePriceWrapper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginBottom: "20px",
  },
  panditImage: {
    width: "110px",
    height: "110px",
    borderRadius: "50%",
    border: "3px solid #d4af37",
    objectFit: "cover",
    marginBottom: "16px",
    backgroundColor: "#fff", // Fallback color before image loads
  },
  priceTag: {
    background: "linear-gradient(90deg, #fef08a 0%, #eab308 100%)",
    color: "#000",
    padding: "8px 24px",
    borderRadius: "8px",
    textAlign: "center",
    width: "100%",
    boxSizing: "border-box",
  },
  priceTextSmall: { margin: 0, fontSize: 12, fontWeight: "bold", textTransform: "uppercase" },
  priceTextLarge: { margin: 0, fontSize: 36, fontWeight: 900 },
  featuresList: {
    borderTop: "1px solid rgba(212, 175, 55, 0.3)",
    paddingTop: "16px",
    marginBottom: "20px",
  },
  featuresHeading: {
    textAlign: "center",
    color: "#fde047",
    fontWeight: "bold",
    fontSize: "12px",
    margin: "0 0 16px 0",
    letterSpacing: "1px",
  },
  featureItem: {
    display: "flex",
    alignItems: "flex-start",
    fontSize: "13px",
    marginBottom: "12px",
    lineHeight: "1.4",
  },
  icon: { marginRight: "10px", fontSize: "16px" },
  bookButton: {
    width: "100%",
    backgroundColor: "#eab308",
    color: "#000",
    fontWeight: "bold",
    padding: "14px 16px",
    borderRadius: "12px",
    border: "none",
    cursor: "pointer",
    fontSize: "14px",
  },
  privateText: {
    textAlign: "center",
    fontSize: "11px",
    color: "#9ca3af",
    marginTop: "12px",
    marginBottom: "0",
  },
};